/**
 * Pure compass math for 360 panorama viewers.
 *
 * Convention (matches Pannellum):
 *   - `yaw` is in degrees; Pannellum's getYaw() returns values in [-180, 180].
 *   - `northOffset` is the compass azimuth (degrees clockwise from true north)
 *     of the panorama's yaw=0 direction. For example, if the center of the
 *     panorama faces east, northOffset = 90. This matches the meaning
 *     documented at node_modules/pannellum/doc/json-config-parameters.md:188
 *     and used by Pannellum's built-in compass at pannellum.js:1496
 *     (`rotate(-config.yaw - config.northOffset)`).
 *   - `heading` is the compass direction (0-360 clockwise from north) the
 *     viewer is currently facing: heading = (yaw + northOffset) mod 360.
 *   - To face north: set yaw = -northOffset.
 *
 * Round 7 note: Rounds 1-6 used the wrong sign convention on `northOffset`
 * (heading = yaw - northOffset), which caused a constant 2*northOffset error
 * versus Pannellum's built-in compass. Fixed here by flipping the sign.
 *
 * These functions are pure and isolated from any viewer library so they can be
 * unit-tested without a DOM. See __tests__/compass.spec.js.
 */

export const CARDINALS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

// -----------------------------------------------------------------------------
// Round 6 timing + threshold constants. Previously hardcoded inside
// forgeService.js and PanoramaCompass.vue — pulled out here so both files share
// a single source of truth.
// -----------------------------------------------------------------------------

/** Duration of the click-to-snap-north animation in ms. */
export const SNAP_DURATION_MS = 600;

/** Extra ms added on top of SNAP_DURATION_MS before declaring snap finished
 * (belt-and-braces for transitionend that doesn't fire on identical transforms). */
export const SNAP_COAST_SLACK_MS = 50;

/** After isInteracting becomes false, wait this long before fading the compass
 * into its idle state. Tuned to be long enough that brief user pauses don't
 * trigger the fade, short enough that idle-state is visible on inspection. */
export const IDLE_FADE_DELAY_MS = 5000;

/** Delay before executing a single-click action, to give a potential double
 * click time to arrive and cancel it. Matches browser double-click detection. */
export const SINGLE_CLICK_DELAY_MS = 250;

/** Emerald pulse on the N tick after a successful snap. Short and subtle. */
export const N_TICK_PULSE_MS = 200;

/** Pitch magnitude (degrees) above which lookAtNorth levels to 0 to avoid
 * disorienting the user who was looking straight up or down. */
export const PITCH_LEVEL_THRESHOLD_DEG = 60;

/**
 * Convert a viewer yaw + northOffset into a 0-360 heading clockwise from north.
 *
 * Pannellum convention: heading = (yaw + northOffset) mod 360. The double-mod
 * pattern `((x % 360) + 360) % 360` is required because JS's `%` operator
 * preserves sign — a single `x % 360` on a negative number yields a negative
 * result. Since Pannellum's getYaw() returns values in [-180, 180] and
 * northOffset can be negative (computeNorthOffset produces negatives when
 * lockedYaw exceeds the lock bearing), (yaw + northOffset) can range into the negatives, which
 * a single mod would not normalize correctly.
 *
 * @param {number} yaw — Pannellum yaw in degrees
 * @param {number} northOffset — compass azimuth of yaw=0 direction, in degrees
 * @returns {number} heading in [0, 360)
 */
export function normalizeHeading(yaw, northOffset) {
  return (((yaw + northOffset) % 360) + 360) % 360;
}

/**
 * Return the cardinal label for a heading, using half-open buckets of 45°.
 * Boundary at 22.5° is assigned to NE (not N) so buckets are unambiguous.
 *
 * @param {number} heading in [0, 360)
 * @returns {string} one of CARDINALS
 */
export function cardinalFor(heading) {
  return CARDINALS[Math.floor((heading + 22.5) / 45) % 8];
}

/**
 * CSS rotation (clockwise-positive degrees) to apply to a compass rose that
 * has N rendered at the 12 o'clock position, so that after rotation the N
 * label points toward true north relative to the viewer's forward direction.
 *
 * Pannellum convention: rotation = -(yaw + northOffset) = -heading. At the
 * yaw where the user faces north (yaw = -northOffset), rotation is 0 and N
 * stays at the top. As yaw increases (user rotates right), rotation becomes
 * more negative → CSS rotate() spins the rose counter-clockwise → N moves
 * to the LEFT of the compass, which is visually correct for "user is facing
 * east". This is the same formula Pannellum's built-in compass uses at
 * pannellum.js:1496 (`-config.yaw - config.northOffset`).
 *
 * Returned unnormalized (not wrapped to [0, 360)) so CSS `rotate()` takes
 * the shortest visual path as yaw crosses 0/360 boundaries.
 *
 * @param {number} yaw — Pannellum yaw in degrees
 * @param {number} northOffset — compass azimuth of yaw=0 direction, in degrees
 * @returns {number} rotation in degrees (clockwise-positive, unnormalized)
 */
export function roseRotation(yaw, northOffset) {
  return -(yaw + northOffset);
}

/**
 * Format a heading for human display: zero-padded integer degrees + cardinal.
 * Example: formatHeading(43.2) → "043° NE"
 *
 * @param {number} heading in [0, 360)
 * @returns {string}
 */
export function formatHeading(heading) {
  const padded = String(Math.round(heading) % 360).padStart(3, '0');
  return `${padded}° ${cardinalFor(heading)}`;
}

/**
 * Decide the target pitch for a snap-to-north action. When the user is looking
 * near straight-up or straight-down, the snap also levels pitch to 0 because
 * arriving at north while still looking at the floor is disorienting. When the
 * user's pitch is moderate, it is preserved (they probably want to stay at that
 * pitch after re-orienting).
 *
 * @param {number} currentPitch — current viewer pitch in degrees
 * @param {boolean} force — if true, always level to 0 regardless of magnitude
 *   (corresponds to the double-click "level view" action).
 * @param {number} [threshold=PITCH_LEVEL_THRESHOLD_DEG]
 * @returns {number} target pitch in degrees
 */
export function levelPitch(currentPitch, force, threshold = PITCH_LEVEL_THRESHOLD_DEG) {
  if (force) return 0;
  if (!Number.isFinite(currentPitch)) return 0;
  return Math.abs(currentPitch) > threshold ? 0 : currentPitch;
}

// -----------------------------------------------------------------------------
// Direction → bearing conversion
//
// Converts a Forge 3D direction vector into a compass bearing so that
// northOffset can be computed from the *actual* lockedForgeDirection instead
// of a hardcoded constant.  The previous formula (`-lockedYaw + 90`) assumed
// the locked direction was always East (90°), but the default lock direction
// is `(-1, 0, 0)` = West (270°), which produced a constant 180° error for
// all auto-imported scans (FARO, Leica, GeoSlam).
// -----------------------------------------------------------------------------

/** Default bearing used when lockedForgeDirection is null or degenerate.
 *  270° = West, matching `Image360.getDefaultLockDirection() → (-1, 0, 0)`. */
const DEFAULT_LOCK_BEARING = 270;

/**
 * Convert a Forge 3D direction vector to a compass bearing (0-360, CW from
 * North).  Projects onto the XY plane (drops Z, Forge's up-axis) and measures
 * the clockwise angle from +Y (North).
 *
 * This mirrors `getCameraAngle()` in forgeService.js but as a pure function
 * that can be unit-tested without a viewer instance.
 *
 * @param {{x: number, y: number}} direction — Forge direction vector (only x, y used)
 * @returns {number} bearing in [0, 360)
 */
export function directionToBearing(direction) {
  if (!direction) return DEFAULT_LOCK_BEARING;
  const dx = direction.x;
  const dy = direction.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-9) return DEFAULT_LOCK_BEARING; // degenerate zero-length
  const dot = dy / len; // dot with (0, 1) unit north vector = dy / len
  let angle = Math.acos(Math.max(-1, Math.min(1, dot))) * (180 / Math.PI);
  if (dx < 0) angle = 360 - angle;
  return angle;
}

/**
 * Compute Pannellum `northOffset` from the image's lock state.
 *
 * `northOffset` = compass bearing of the panorama's yaw=0 direction.
 * At yaw = lockedYaw the viewer faces `lockedForgeDirection`, whose bearing
 * we compute dynamically.  yaw=0 is `lockedYaw` degrees *before* that
 * direction, so:  northOffset = bearing(lockedForgeDirection) − lockedYaw.
 *
 * @param {number} lockedYaw — Pannellum yaw captured at lock time
 * @param {{x: number, y: number}|null} lockedForgeDirection — Forge direction at lock time
 * @returns {number} northOffset in degrees (not normalized to [0,360))
 */
export function computeNorthOffset(lockedYaw, lockedForgeDirection) {
  return -lockedYaw + directionToBearing(lockedForgeDirection);
}
